import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { thumbUrl, formatApiError } from "@/lib/utils-app";
import { PawPrintIcon as PawPrint, DogIcon as Dog, FootprintsIcon as Footprints, HouseIcon as House, ScissorsIcon as Scissors, GraduationCapIcon as GraduationCap, ArrowLeftIcon as ArrowLeft, CheckIcon as Check, XIcon as X, SyringeIcon as Syringe, UploadSimpleIcon as UploadSimple, FileTextIcon as FileText, TrashIcon as Trash } from "@phosphor-icons/react";

export const PASSPORT_LABELS = {
  temperament: "Temperament", good_with_dogs: "Good with dogs", good_with_cats: "Good with cats",
  good_with_kids: "Good with kids", vet_name: "Vet", vet_phone: "Vet phone",
  emergency_name: "Emergency contact", emergency_phone: "Emergency phone",
  meds: "Medications", quirks: "Quirks & notes",
  leash_behavior: "Leash behavior", triggers: "Reactivity triggers", equipment: "Walking gear",
  pace: "Pace", recall_ok: "Off-leash recall", weather_notes: "Weather limits", treats_ok: "Treats on walks",
  feeding: "Feeding", overnight_routine: "Overnight routine", crate: "Crate",
  alone_tolerance: "Alone time", house_rules: "House rules",
  coat_type: "Coat type", matting_areas: "Matting-prone areas", skin_conditions: "Skin conditions",
  sensitivities: "Handling sensitivities", bite_history: "Bite history", muzzle_needed: "Muzzle needed",
  calming: "Calms down with", usual_cut: "Usual cut", nail_pref: "Nails", fragrance_free: "Fragrance-free",
  goals: "Training goals", known_commands: "Known commands", motivators: "Motivated by",
  reactivity_details: "Reactivity details", bite_incident_history: "Bite / incident history",
  past_training: "Past training",
  rabies_current: "Rabies current", dhpp_current: "DHPP/DA2PP current", bordetella_current: "Bordetella current",
  rabies_expires: "Rabies expires", dhpp_expires: "DHPP expires",
  flea_tick_preventative: "Flea/tick preventative", notes: "Vaccination notes", documents: "Uploaded records",
};

function Chip({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`rounded-full px-3 py-1.5 text-[12px] font-medium border transition-colors ${
        active ? "bg-ink text-white border-ink" : "bg-white text-[#3a3a3a] border-[#E5E5E5] hover:border-ink"}`}>
      {children}
    </button>
  );
}

function ChipMulti({ options, value = [], onChange, testId }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((o) => <Chip key={o} active={value.includes(o)} onClick={() => toggle(o)}>{o}</Chip>)}
    </div>
  );
}

function ChoiceRow({ options, value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((o) => <Chip key={o} active={value === o} onClick={() => onChange(value === o ? "" : o)}>{o}</Chip>)}
    </div>
  );
}

function YesNo({ value, onChange, testId }) {
  return (
    <div className="flex gap-1.5" data-testid={testId}>
      <Chip active={value === true} onClick={() => onChange(value === true ? null : true)}>Yes</Chip>
      <Chip active={value === false} onClick={() => onChange(value === false ? null : false)}>No</Chip>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

const SECTIONS = [
  {
    key: "core", title: "The Basics", Icon: PawPrint, color: "#B8860B",
    blurb: "Every pro sees this — the essentials for anyone caring for your dog.",
  },
  {
    key: "walking", title: "Walking", Icon: Footprints, color: "#0F8043",
    blurb: "What walkers see — leash style, triggers, and gear.",
  },
  {
    key: "sitting", title: "Sitting & Boarding", Icon: House, color: "#C1272D",
    blurb: "What sitters see — routines, feeding, and house rules.",
  },
  {
    key: "grooming", title: "Grooming", Icon: Scissors, color: "#8A5CB8",
    blurb: "What groomers see — coat, sensitivities, and the usual cut.",
  },
  {
    key: "training", title: "Training & Behavior", Icon: GraduationCap, color: "#1F5FBF",
    blurb: "What trainers see — goals, known commands, motivators, and behavior history.",
  },
  {
    key: "vaccinations", title: "Vaccinations", Icon: Syringe, color: "#2E6B3E",
    blurb: "Optional — self-reported vaccine status and record uploads that booked pros and field hosts can see.",
  },
];

function SectionEditor({ section, data, onSave, saving }) {
  const [d, setD] = useState(data || {});
  useEffect(() => setD(data || {}), [data]);
  const set = (k, v) => setD((prev) => ({ ...prev, [k]: v }));
  const { key } = section;

  return (
    <div className="space-y-4">
      {key === "core" && (
        <>
          <F label="Temperament">
            <ChipMulti testId="pp-temperament" value={d.temperament} onChange={(v) => set("temperament", v)}
              options={["Calm", "Playful", "Anxious", "Reactive", "Protective", "Social butterfly", "Shy"]} />
          </F>
          <div className="grid grid-cols-3 gap-3">
            <F label="Good with dogs"><YesNo testId="pp-good-dogs" value={d.good_with_dogs} onChange={(v) => set("good_with_dogs", v)} /></F>
            <F label="Good with cats"><YesNo testId="pp-good-cats" value={d.good_with_cats} onChange={(v) => set("good_with_cats", v)} /></F>
            <F label="Good with kids"><YesNo testId="pp-good-kids" value={d.good_with_kids} onChange={(v) => set("good_with_kids", v)} /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Vet name"><Input data-testid="pp-vet-name" value={d.vet_name || ""} onChange={(e) => set("vet_name", e.target.value)} /></F>
            <F label="Vet phone"><Input data-testid="pp-vet-phone" value={d.vet_phone || ""} onChange={(e) => set("vet_phone", e.target.value)} /></F>
            <F label="Emergency contact"><Input data-testid="pp-em-name" value={d.emergency_name || ""} onChange={(e) => set("emergency_name", e.target.value)} /></F>
            <F label="Emergency phone"><Input data-testid="pp-em-phone" value={d.emergency_phone || ""} onChange={(e) => set("emergency_phone", e.target.value)} /></F>
          </div>
          <F label='Medications (or "none")'>
            <Textarea data-testid="pp-meds" rows={2} value={d.meds || ""} onChange={(e) => set("meds", e.target.value)}
              placeholder="e.g. Apoquel 16mg with breakfast" />
          </F>
          <F label="Quirks & notes">
            <Textarea data-testid="pp-quirks" rows={2} value={d.quirks || ""} onChange={(e) => set("quirks", e.target.value)}
              placeholder="Hates skateboards. Hides under the bed during thunder." />
          </F>
        </>
      )}
      {key === "walking" && (
        <>
          <F label="Leash behavior">
            <ChoiceRow testId="pp-leash" value={d.leash_behavior} onChange={(v) => set("leash_behavior", v)}
              options={["Loose leash", "Pulls a little", "Pulls hard", "Lunges sometimes"]} />
          </F>
          <F label="Reactivity triggers (tap all that apply)">
            <ChipMulti testId="pp-triggers" value={d.triggers} onChange={(v) => set("triggers", v)}
              options={["Other dogs", "Bikes", "Skateboards", "Squirrels", "Cars", "Strangers", "Kids running", "Loud noises"]} />
          </F>
          <F label="Walking gear">
            <ChoiceRow testId="pp-equipment" value={d.equipment} onChange={(v) => set("equipment", v)}
              options={["Harness (back clip)", "Harness (front clip)", "Flat collar", "Martingale", "Head halter"]} />
          </F>
          <F label="Preferred pace">
            <ChoiceRow testId="pp-pace" value={d.pace} onChange={(v) => set("pace", v)}
              options={["Sniffy stroll", "Steady walk", "Power walk", "Senior pace"]} />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Off-leash OK in safe areas?"><YesNo testId="pp-recall" value={d.recall_ok} onChange={(v) => set("recall_ok", v)} /></F>
            <F label="Treats allowed on walks?"><YesNo testId="pp-treats" value={d.treats_ok} onChange={(v) => set("treats_ok", v)} /></F>
          </div>
          <F label="Weather limits">
            <Input data-testid="pp-weather" value={d.weather_notes || ""} onChange={(e) => set("weather_notes", e.target.value)}
              placeholder="Heat sensitive — short walks above 80°F" />
          </F>
        </>
      )}
      {key === "sitting" && (
        <>
          <F label="Feeding schedule">
            <Textarea data-testid="pp-feeding" rows={2} value={d.feeding || ""} onChange={(e) => set("feeding", e.target.value)}
              placeholder="1 cup kibble at 7am and 6pm. Slow feeder bowl." />
          </F>
          <F label="Overnight routine">
            <Textarea data-testid="pp-overnight" rows={2} value={d.overnight_routine || ""} onChange={(e) => set("overnight_routine", e.target.value)}
              placeholder="Last potty at 10pm, sleeps in crate, morning walk by 8am." />
          </F>
          <F label="Crate">
            <ChoiceRow testId="pp-crate" value={d.crate} onChange={(v) => set("crate", v)}
              options={["Crate trained — sleeps in crate", "Crate for alone time only", "No crate"]} />
          </F>
          <F label="Alone-time tolerance">
            <ChoiceRow testId="pp-alone" value={d.alone_tolerance} onChange={(v) => set("alone_tolerance", v)}
              options={["Fine alone 4+ hrs", "1–3 hrs max", "Needs company"]} />
          </F>
          <F label="House rules">
            <Textarea data-testid="pp-house" rows={2} value={d.house_rules || ""} onChange={(e) => set("house_rules", e.target.value)}
              placeholder="No couch. Front door dasher — leash on before opening." />
          </F>
        </>
      )}
      {key === "grooming" && (
        <>
          <F label="Coat type">
            <ChoiceRow testId="pp-coat" value={d.coat_type} onChange={(v) => set("coat_type", v)}
              options={["Smooth", "Double coat", "Curly / doodle", "Wire", "Long silky", "Hairless"]} />
          </F>
          <F label="Matting-prone areas">
            <ChipMulti testId="pp-matting" value={d.matting_areas} onChange={(v) => set("matting_areas", v)}
              options={["Ears", "Armpits", "Belly", "Tail", "Rear", "Legs"]} />
          </F>
          <F label="Handling sensitivities">
            <ChipMulti testId="pp-sensitivities" value={d.sensitivities} onChange={(v) => set("sensitivities", v)}
              options={["Paw handling", "Ears", "Clippers", "Dryer", "Nail trims", "Bathing"]} />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Any bite history?"><YesNo testId="pp-bite" value={d.bite_history} onChange={(v) => set("bite_history", v)} /></F>
            <F label="Muzzle needed?"><YesNo testId="pp-muzzle" value={d.muzzle_needed} onChange={(v) => set("muzzle_needed", v)} /></F>
          </div>
          <F label="Calms down with">
            <ChipMulti testId="pp-calming" value={d.calming} onChange={(v) => set("calming", v)}
              options={["Treats", "Breaks", "Soft voice", "Owner item nearby"]} />
          </F>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Usual cut"><Input data-testid="pp-cut" value={d.usual_cut || ""} onChange={(e) => set("usual_cut", e.target.value)} placeholder='e.g. "Teddy bear, 1 inch"' /></F>
            <F label="Nails">
              <ChoiceRow testId="pp-nails" value={d.nail_pref} onChange={(v) => set("nail_pref", v)}
                options={["Clipping fine", "Grinding preferred", "Very difficult"]} />
            </F>
          </div>
          <F label="Fragrance-free products?"><YesNo testId="pp-fragrance" value={d.fragrance_free} onChange={(v) => set("fragrance_free", v)} /></F>
        </>
      )}
      {key === "training" && (
        <>
          <F label="Training goals">
            <Textarea data-testid="pp-tr-goals" rows={2} value={d.goals || ""} onChange={(e) => set("goals", e.target.value)}
              placeholder="Stop leash pulling; solid recall; settle around guests." />
          </F>
          <F label="Known commands">
            <ChipMulti testId="pp-tr-commands" value={d.known_commands} onChange={(v) => set("known_commands", v)}
              options={["Sit", "Down", "Stay", "Come", "Leave it", "Drop it", "Heel", "Place", "Crate up"]} />
          </F>
          <F label="Motivated by">
            <ChipMulti testId="pp-tr-motivators" value={d.motivators} onChange={(v) => set("motivators", v)}
              options={["Treats", "Toys", "Praise", "Play", "Tug"]} />
          </F>
          <F label="Reactivity details">
            <Textarea data-testid="pp-tr-reactivity" rows={2} value={d.reactivity_details || ""} onChange={(e) => set("reactivity_details", e.target.value)}
              placeholder="Barks and lunges at other dogs on leash; fine off-leash." />
          </F>
          <F label='Bite / incident history (or "none")'>
            <Textarea data-testid="pp-tr-bites" rows={2} value={d.bite_incident_history || ""} onChange={(e) => set("bite_incident_history", e.target.value)}
              placeholder="Being upfront helps trainers plan a safe session." />
          </F>
          <F label="Past training">
            <Textarea data-testid="pp-tr-past" rows={2} value={d.past_training || ""} onChange={(e) => set("past_training", e.target.value)}
              placeholder="Puppy class in 2024; three private sessions for leash skills." />
          </F>
        </>
      )}
      {key === "vaccinations" && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <F label="Rabies current?"><YesNo testId="pp-vax-rabies" value={d.rabies_current} onChange={(v) => set("rabies_current", v)} /></F>
            <F label="DHPP current?"><YesNo testId="pp-vax-dhpp" value={d.dhpp_current} onChange={(v) => set("dhpp_current", v)} /></F>
            <F label="Bordetella?"><YesNo testId="pp-vax-bordetella" value={d.bordetella_current} onChange={(v) => set("bordetella_current", v)} /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Rabies expires"><Input type="date" data-testid="pp-vax-rabies-exp" value={d.rabies_expires || ""} onChange={(e) => set("rabies_expires", e.target.value)} /></F>
            <F label="DHPP expires"><Input type="date" data-testid="pp-vax-dhpp-exp" value={d.dhpp_expires || ""} onChange={(e) => set("dhpp_expires", e.target.value)} /></F>
          </div>
          <F label="Flea / tick / heartworm preventative">
            <Input data-testid="pp-vax-flea" value={d.flea_tick_preventative || ""} onChange={(e) => set("flea_tick_preventative", e.target.value)}
              placeholder="e.g. Simparica Trio monthly" />
          </F>
          <F label="Notes">
            <Textarea data-testid="pp-vax-notes" rows={2} value={d.notes || ""} onChange={(e) => set("notes", e.target.value)}
              placeholder="Titers instead of boosters; vet letter uploaded below." />
          </F>
          <F label="Upload records (optional — photo or PDF)">
            <VaxDocsUploader docs={d.documents || []} onChange={(docs) => set("documents", docs)} />
          </F>
          <p className="text-[11px] text-[#6B6B6B] leading-relaxed">
            Self-reported by you and not verified by Armstrong K9 Registry. Booked pros and field hosts can view this section to prepare for your visit.
          </p>
        </>
      )}
      <Button onClick={() => onSave(key, d)} disabled={saving}
        className="rounded-full bg-ink hover:bg-crimson text-white" data-testid={`pp-save-${key}`}>
        {saving ? "Saving…" : `Save ${section.title.toLowerCase()}`}
      </Button>
    </div>
  );
}

export default function CarePassport() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState(() => {
    const s = new URLSearchParams(window.location.search).get("section");
    return SECTIONS.some((x) => x.key === s) ? s : "core";
  });

  const load = () => api.get(`/passports/${id}`).then((r) => setP(r.data)).catch(() => setP(null));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const save = async (section, data) => {
    setSaving(true);
    try {
      const r = await api.put(`/passports/${id}`, { [section]: data });
      track("passport_section_saved", { section, completion: r.data.completion.overall });
      toast.success(`Saved — passport ${r.data.completion.overall}% complete`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const actSuggestion = async (sid, action) => {
    try {
      await api.post(`/passports/suggestions/${sid}/${action}`);
      toast.success(action === "approve" ? "Added to the passport" : "Dismissed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!p) return <AppShell><div className="py-16 text-center text-sm text-[#6B6B6B]">Loading passport…</div></AppShell>;
  const comp = p.completion || {};

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto">
        <Link to={`/dogs/${id}`} className="inline-flex items-center gap-1 text-xs text-[#666] hover:text-ink mb-3" data-testid="passport-back">
          <ArrowLeft size={12} /> Back to {p.dog?.name}
        </Link>
        <div className="flex items-center gap-4 mb-2">
          {p.dog?.photo ? (
            <img src={thumbUrl(p.dog.photo) || p.dog.photo} alt={p.dog?.name} className="w-16 h-16 rounded-full object-cover border-2 border-gold" />
          ) : (
            <span className="w-16 h-16 rounded-full bg-cream border-2 border-gold flex items-center justify-center"><Dog size={28} weight="duotone" className="text-gold" /></span>
          )}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">Care Passport</p>
            <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight leading-tight">{p.dog?.name}</h1>
            <p className="text-[12.5px] text-[#6B6B6B]">{p.dog?.breed}</p>
          </div>
          <div className="ml-auto text-right">
            <p className="font-spectral text-3xl text-ink" data-testid="passport-overall">{comp.overall ?? 0}%</p>
            <p className="text-[10.5px] uppercase tracking-wider text-[#6B6B6B]">complete</p>
          </div>
        </div>
        <p className="text-[13px] text-[#6B6B6B] mb-5 leading-relaxed">
          Fill this once — every trainer, groomer, walker, and sitter you book automatically sees the part that matters for their job.
        </p>

        {(p.suggestions || []).length > 0 && (
          <div className="mb-5 rounded-xl border border-gold/40 bg-cream/60 p-4" data-testid="passport-suggestions">
            <p className="text-[11px] uppercase tracking-wider font-bold text-gold mb-2">Suggested by your pros</p>
            {p.suggestions.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 py-2 border-t border-gold/20 first:border-0">
                <p className="text-[13px] text-ink">“{s.note}” <span className="text-[#6B6B6B]">— {s.provider_name}</span></p>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" className="rounded-full h-7 px-3 bg-[#0F8043] hover:bg-[#0c6a37] text-white" onClick={() => actSuggestion(s.id, "approve")} data-testid={`sug-approve-${s.id}`}>
                    <Check size={12} weight="bold" />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full h-7 px-3" onClick={() => actSuggestion(s.id, "dismiss")} data-testid={`sug-dismiss-${s.id}`}>
                    <X size={12} weight="bold" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3">
          {SECTIONS.map((s) => {
            const open = openSection === s.key;
            return (
              <div key={s.key} className="bg-white border border-[#EAEAEA] rounded-xl overflow-hidden">
                <button type="button" onClick={() => setOpenSection(open ? "" : s.key)}
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-cream/40 transition-colors"
                  data-testid={`pp-section-${s.key}`}>
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${s.color}18` }}>
                    <s.Icon size={18} weight="duotone" style={{ color: s.color }} />
                  </span>
                  <span className="flex-1">
                    <span className="font-spectral text-lg text-ink block leading-tight">{s.title}</span>
                    <span className="text-[11.5px] text-[#6B6B6B]">{s.blurb}</span>
                  </span>
                  <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${
                    (comp[s.key] ?? 0) >= 70 ? "bg-[#0F8043]/10 text-[#0F8043]" : "bg-cream text-gold"}`}>
                    {comp[s.key] ?? 0}%
                  </span>
                </button>
                {open && (
                  <div className="p-4 pt-1 border-t border-[#F0F0F0]">
                    <SectionEditor section={s} data={p[s.key]} onSave={save} saving={saving} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-[#9A9A9A] mt-4">
          Only you and providers you've booked can see this passport. Every field is optional.
        </p>
      </div>
    </AppShell>
  );
}

function VaxDocsUploader({ docs, onChange }) {
  const [uploading, setUploading] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Max file size is 10 MB.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...(docs || []), { name: file.name, storage_path: r.data.storage_path }].slice(0, 10));
      toast.success("Record uploaded — remember to save the section");
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-2" data-testid="pp-vax-docs">
      {(docs || []).map((doc, i) => (
        <div key={doc.storage_path} className="flex items-center gap-2 bg-cream border border-[#E2DAC2] rounded-lg px-3 py-2">
          <FileText size={15} weight="duotone" className="text-[#2E6B3E] shrink-0" />
          <span className="text-[12.5px] text-ink truncate flex-1">{doc.name || `Record ${i + 1}`}</span>
          <button type="button" onClick={() => onChange(docs.filter((x) => x.storage_path !== doc.storage_path))}
            className="text-[#C1272D] hover:opacity-70" data-testid={`pp-vax-doc-remove-${i}`} aria-label="Remove record">
            <Trash size={14} />
          </button>
        </div>
      ))}
      <label className={`inline-flex items-center gap-2 rounded-full border border-[#E5E5E5] px-4 py-2 text-[12.5px] font-medium cursor-pointer hover:border-ink transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
        <UploadSimple size={14} weight="bold" />
        {uploading ? "Uploading…" : "Add record"}
        <input type="file" accept="image/*,.pdf" className="hidden" onChange={pick} data-testid="pp-vax-doc-input" />
      </label>
    </div>
  );
}
